import React, { useState, useEffect, useRef, use } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Pagination,
  AppBar,
  Toolbar,
  Badge,
  CircularProgress,
  useTheme,
  useMediaQuery,
  styled,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import LeftToolbar from "./LeftToolbar";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TextEditorComponent from "./TextEditorComponent";
import SearchIcon from "@mui/icons-material/Search";
import NotificationSnackbar from "./NotificationBar";
import { alpha } from "@mui/material/styles";
import ConfirmationDialog from "./ConfirmationDialog";
import DOMPurify from "dompurify";
import { useSelector } from "react-redux";
import { jsonToHtml } from "./utils/tiptapConfig";

const MainContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: "#f5f7fa",
});

const MainContent = styled(Box)(({ theme, $collapsed }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: $collapsed ? "80px" : "280px",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    padding: theme.spacing(2),
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  overflowX: "auto",
  [theme.breakpoints.down("sm")]: {
    borderRadius: "8px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const ModernTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  "&:first-of-type": {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

function Articles() {
  const [articles, setArticles] = useState([]);

  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);
  const [completeArticles, setCompleteArticles] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [openConfirm, setOpenConfirm] = useState(false);
 
  const [loadingArticleId, setLoadingArticleId] = useState(null);
  const [loadingArticleId1, setLoadingArticleId1] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    moduleId: "",
    content: null,
    htmlContent: "",
  });
  const [formErrors, setFormErrors] = useState({
    title: false,
    moduleId: false,
    content: false,
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(5);
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("Articles");
 
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isDraftRestored, setIsDraftRestored] = useState(false);

  const createEditorRef = useRef(null);
  const editEditorRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Constants for localStorage keys
  const DRAFT_ARTICLE_KEY = 'draft_article_data';

// Function to format date as day-month-year (29-09-2025)
const formatDateDDMMYYYY = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return "N/A";
  }
};

// Function to save form data to localStorage
const saveDraftToStorage = (formData) => {
    try {
      localStorage.setItem(DRAFT_ARTICLE_KEY, JSON.stringify(formData));
    } catch (error) {
      console.error('Error saving draft to localStorage:', error);
    }
  };

  // Function to load form data from localStorage
  const loadDraftFromStorage = () => {
    try {
      const savedDraft = localStorage.getItem(DRAFT_ARTICLE_KEY);
      return savedDraft ? JSON.parse(savedDraft) : null;
    } catch (error) {
      console.error('Error loading draft from localStorage:', error);
      return null;
    }
  };

  // Function to clear saved draft
  const clearDraftFromStorage = () => {
    try {
      localStorage.removeItem(DRAFT_ARTICLE_KEY);
    } catch (error) {
      console.error('Error clearing draft from localStorage:', error);
    }
  };

  const [user] = useState(() => {
    try {
      const userData = localStorage.getItem("userData");
      return userData ? JSON.parse(userData) : { id: 1, username: "Guest" };
    } catch (error) {
      console.error("Error parsing user data:", error);
      return { id: 1, username: "Guest" };
    }
  });

  // Get data from Redux store
  const ReduxArticles = useSelector(
    (state) => state.support.filteredArticles || []
  );
  const ReduxCategories = useSelector(
    (state) => state.support.categories || []
  );
  
  const isLoading = useSelector((state) => state.support.loading.global);

  useEffect(() => {
    const fetchArticles = async () => {
      const newReduxArticles = await Promise.all(
        ReduxArticles.map(async (article) => {
          try {
            return {
              ...article, // Keep all original article data

              title: article.title || "Untitled Article",
              createdBy: article.createdBy || "Unknown Author",
              createdOn: article.createdOn || new Date().toISOString(),
              categoryName: article.categoryName || "Uncategorized",
              //content: response.data.article || 'Content unavailable',
            };
          } catch (error) {
            console.error(`Error fetching article ${article.id}:`, error);
            return {
              ...article,
              title: article.title || "Untitled Article",
              createdBy: article.createdBy || "Unknown Author",
              createdOn: article.createdOn || new Date().toISOString(),
              categoryName: article.categoryName || "Uncategorized",
              content: "Content unavailable",
            };
          }
        })
      );

      setCompleteArticles(newReduxArticles);
      console.log("Complete Articles:", newReduxArticles);
    };

    if (ReduxArticles.length > 0) {
      fetchArticles();
    }
  }, [ReduxArticles, ReduxCategories]); // Added ReduxArticles to dependency array

  // Function to handle opening the create modal and restoring draft data
  const handleOpenCreateModal = () => {
    const savedDraft = loadDraftFromStorage();
    if (savedDraft) {
      setFormData(savedDraft);
      setIsDraftRestored(true);
      // Set editor content after a small delay to ensure editor is ready
      setTimeout(() => {
        if (createEditorRef.current && savedDraft.content) {
          createEditorRef.current.setContents(savedDraft.content);
        }
      }, 100);
    } else {
      setIsDraftRestored(false);
    }
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    // Don't clear form data here - keep it for when user reopens modal
    // Only clear validation errors
    setFormErrors({
      title: false,
      moduleId: false,
      content: false,
    });
  };

  // Function to completely reset the form (used after successful creation)
  const resetFormCompletely = () => {
    setFormData({
      title: "",
      moduleId: "",
      content: null,
      htmlContent: "",
    });
    setFormErrors({
      title: false,
      moduleId: false,
      content: false,
    });
    setIsDraftRestored(false);
    if (createEditorRef.current) {
      createEditorRef.current.clearEditor();
    }
    clearDraftFromStorage();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    
    // Save to localStorage whenever form data changes
    saveDraftToStorage(updatedFormData);
    
    if (value) {
      setFormErrors({ ...formErrors, [name]: false });
    }
  };

  const handleEditorChange = ({ html, json }) => {
    const updatedFormData = {
      ...formData,
      content: json,
      htmlContent: html,
    };
    
    setFormData(updatedFormData);
    
    // Save to localStorage whenever editor content changes
    saveDraftToStorage(updatedFormData);

    const isContentTrulyEmpty =
      !json ||
      json.content.length === 0 ||
      (json.content.length === 1 &&
        json.content[0].type === "paragraph" &&
        !json.content[0].content);

    setFormErrors((prev) => ({ ...prev, content: isContentTrulyEmpty }));
  };

  const validateForm = async () => {
    const errors = {
      title: !formData.title.trim(),
      moduleId: !formData.moduleId,
      content:
        !formData.content ||
        formData.content.content.length === 0 ||
        (formData.content.content.length === 1 &&
          formData.content.content[0].type === "paragraph" &&
          !formData.content.content[0].content),
    };
    setFormErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const handleCreateArticle = async () => {
    if (!(await validateForm())) {
      handleSnackbarOpen("Please fill all required fields", "error");
      return;
    }

    const userToken = localStorage.getItem("userToken");
    if (!userToken) {
      handleSnackbarOpen("Unauthorized. Please login first.", "error");
      return;
    }

    setLoading(true);
    try {
      const currentEditorContentJson = createEditorRef.current?.getContents();

      if (
        !currentEditorContentJson ||
        currentEditorContentJson.content.length === 0 ||
        (currentEditorContentJson.content.length === 1 &&
          currentEditorContentJson.content[0].type === "paragraph" &&
          !currentEditorContentJson.content[0].content)
      ) {
        handleSnackbarOpen("Content cannot be empty.", "error");
        setFormErrors((prev) => ({ ...prev, content: true }));
        setLoading(false);
        return;
      }

      const payload = {
        title: formData.title.trim(),
        moduleId: parseInt(formData.moduleId),
        content: JSON.stringify(currentEditorContentJson),
        createdBy: user.id,
      };
      console.log("Payload for creating article:", payload);

      const res = await fetch("/api/support/create-article", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${userToken}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to create article");
      }

      const newArticle = {
        id: data.result?.recordset?.[0]?.id,
        title: formData.title,
        content: currentEditorContentJson,
        htmlContent: formData.htmlContent,
        moduleId: parseInt(formData.moduleId),
        moduleName:
          ReduxCategories.find((c) => c.id === parseInt(formData.moduleId))
            ?.categoryName || "",
        createdBy: user.username || "Unknown",
        createdon: new Date().toISOString(),
      };

      setArticles((prev) => [newArticle, ...prev]);
      handleSnackbarOpen(
        data.message || "Article created successfully!",
        "success"
      );
      resetFormCompletely();
      setCreateModalOpen(false);
    } catch (error) {
      console.error("Error creating article:", error);
      handleSnackbarOpen(error.message || "Failed to create article", "error");
    } finally {
      setLoading(false);
    }
  };

   const handleOpenEditArticleModal = async(article) => {
    setLoadingArticleId1(article.id); // Start loading
    try {
      const response = await axios.get(
        `/api/support/view-single-article/${article.id}`
      );
      const htmlContent = jsonToHtml(response.data.article);
      setFormData({
        id: article.id,
        title: article.title,
        module: article.categoryName,
        content: response.data.article,
        htmlContent: htmlContent || "<p>Content could not be rendered</p>",
      });
      setEditModalOpen(true);
    } catch (error) {
      console.error("Error generating preview:", error);
      handleSnackbarOpen("Failed to generate article preview", "error");
    } finally {
      setLoadingArticleId1(null); // Stop loading
    }
        
  };
   



   const handleUpdateArticle = async () => {
  if (!(await validateForm())) {
    handleSnackbarOpen("Please fill all required fields", "error");
    return;
  }
  
  if (!formData.id) return;
  
  setLoading(true);
  try {
    const currentEditorContentJson = editEditorRef.current?.getContents();
    
    // Validate editor content
    if (
      !currentEditorContentJson ||
      currentEditorContentJson.ops?.length === 0 ||
      (currentEditorContentJson.ops?.length === 1 && 
       currentEditorContentJson.ops[0].insert === '\n')
    ) {
      handleSnackbarOpen("Content cannot be empty.", "error");
      setFormErrors((prev) => ({ ...prev, content: true }));
      setLoading(false);
      return;
    }

    const payload = {
      id: formData.id,
      title: formData.title.trim(),
      moduleId: parseInt(formData.moduleId),
      content: JSON.stringify(currentEditorContentJson), // Stringify the  JSON
    };

    const res = await axios.put("/api/support/update-article", payload);
    
    const updatedArticle = res.data.result;
    
    // Update the articles list in state
    setArticles(articles.map(article => 
      article.id === formData.id 
        ? { 
            ...article, 
            title: payload.title,
            content: currentEditorContentJson, // Keep as parsed JSON
            moduleId: payload.moduleId,
            categoryName: ReduxCategories.find(c => c.id === payload.moduleId)?.categoryName || article.categoryName
          } 
        : article
    ));

    handleSnackbarOpen("Article updated successfully!", "success");
    setEditModalOpen(false);
    setFormData({
      id: "",
      title: "",
      moduleId: "",
      content: "",
      htmlContent: ""
    });
  } catch (error) {
    console.error("Error updating article:", error);
    handleSnackbarOpen(
      error.response?.data?.message || "Failed to update article", 
      "error"
    );
  } finally {
    setLoading(false);
  }
};

  const handlePreview = async (article) => {
    setLoadingArticleId(article.id); // Start loading

    try {
      const response = await axios.get(
        `/api/support/view-single-article/${article.id}`
      );
      const htmlContent = jsonToHtml(response.data.article);
      setFormData({
        id: article.id,
        title: article.title,
        module: article.categoryName,
        content: article.content,
        htmlContent: htmlContent || "<p>Content could not be rendered</p>",
      });
      setPreviewOpen(true);
    } catch (error) {
      console.error("Error generating preview:", error);
      handleSnackbarOpen("Failed to generate article preview", "error");
    } finally {
      setLoadingArticleId(null); // Stop loading
    }
  };

  const handleClosePreview = () => setPreviewOpen(false);

  const handleDeleteClick = (article) => {
    setArticleToDelete(article);
    setOpenConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!articleToDelete) return;

    setLoading(true);

    try {
      const userToken = localStorage.getItem("userToken");
      if (!userToken) {
        handleSnackbarOpen("Unauthorized. Please login first.", "error");
        setLoading(false);
        return;
      }

      const res = await fetch(`/api/support/delete-article/${articleToDelete.id}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${userToken}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to delete article");
      }

      setArticles((prev) =>
        prev.filter((a) => a.id !== Number(articleToDelete.id))
      );
      handleSnackbarOpen(data.message || "Article deleted successfully", "success");
      
      // Refresh the entire page to show the user that changes have taken place
      setTimeout(() => {
        window.location.reload();
      }, 1500); // Wait 1.5 seconds to show the success message before refreshing
    } catch (error) {
      console.error("Delete error:", error);
      handleSnackbarOpen(
        error.message || "Failed to delete article",
        "error"
      );
    } finally {
      setLoading(false);
      setOpenConfirm(false);
      setArticleToDelete(null);
    }
  };

  {
    /*} const filteredArticles = articles.filter(article =>
    (article.title || "").toLowerCase().includes((searchTerm || "").toLowerCase()) ||
    (article.moduleName || "").toLowerCase().includes((searchTerm || "").toLowerCase()) ||
    (article.htmlContent || "").toLowerCase().includes((searchTerm || "").toLowerCase())
  ); */
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleSnackbarOpen = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  return (
    <MainContainer>
      <AppBar
        position="fixed"
        sx={{
          backgroundImage: "linear-gradient(195deg, #0a2e52 0%, #1565C0 200%)",
          boxShadow:
            "0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(0, 0, 0, 0.4)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          left: collapsed ? "80px" : "280px",
          width: `calc(100% - ${collapsed ? "80px" : "280px"})`,
          transition: theme.transitions.create(["width", "left"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          [theme.breakpoints.down("sm")]: {
            left: 0,
            width: "100%",
          },
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
            IPPIS SUPPORT PORTAL
          </Typography>
          <IconButton color="inherit">
            <Badge badgeContent={ReduxArticles.length} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      <LeftToolbar
        onCollapse={setCollapsed}
        selectedMenu={selectedMenu}
        onSelectMenu={setSelectedMenu}
      />

      <MainContent $collapsed={collapsed}>
        <Toolbar />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 2 : 0,
          }}
        >
          <TextField
            label="Search articles..."
            variant="outlined"
            size="small"
            fullWidth={isMobile}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: isMobile ? "100%" : 400,
              "& .MuiOutlinedInput-root": { borderRadius: "12px" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Create new Article">
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenCreateModal}
              startIcon={<AddIcon />}
              size={isMobile ? "small" : "medium"}
            >
              {isMobile ? "Create" : "Create Article"}
            </Button>
          </Tooltip>
        </Box>

        <Box sx={{ flex: 1, p: 2, bgcolor: "#f8f9fa", borderRadius: 2 }}>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "16px",
              p: 3,
              mb: 3,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Articles Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {ReduxArticles.length}{" "}
                {ReduxArticles.length === 1 ? "Article" : "Articles"} Found
              </Typography>
            </Box>

            <ResponsiveTableContainer component={Paper} sx={{ boxShadow: 2 }}>
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: theme.palette.primary.light,
                      "& th": {
                        color: "white",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      },
                    }}
                  >
                    {[
                      "Title",
                      "Module",
                      "Created Date",
                      "Created By",
                      "Actions",
                    ].map((header) => (
                      <ModernTableCell
                        key={header}
                        align={header === "Actions" ? "right" : "left"}
                      >
                        {header}
                      </ModernTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <ModernTableCell
                        colSpan={5}
                        align="center"
                        sx={{ py: 4 }}
                      >
                        <CircularProgress size={32} />
                      </ModernTableCell>
                    </TableRow>
                  ) : completeArticles?.length > 0 ? (
                    completeArticles
                      .slice((page - 1) * rowsPerPage, page * rowsPerPage)
                      .map((article) => (
                        <StyledTableRow key={article.id} hover>
                          <ModernTableCell>
                            <Typography fontWeight="medium">
                              {article.title}
                            </Typography>
                          </ModernTableCell>
                          <ModernTableCell>
                            {article.categoryName}
                          </ModernTableCell>
                          <ModernTableCell>
                            {formatDateDDMMYYYY(
                              article.createdon || article.createdAt
                            )}
                          </ModernTableCell>
                          <ModernTableCell>
                            <Typography variant="body2">
                              {article.createdBy}
                            </Typography>
                          </ModernTableCell>
                          <ModernTableCell align="right">
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 1,
                              }}
                            >
                              <Tooltip title="View Article">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="info"
                                    disabled={loadingArticleId === article.id}
                                    onClick={() => handlePreview(article)}
                                    sx={{
                                      "&:hover": {
                                        backgroundColor: alpha(
                                          theme.palette.info.main,
                                          0.1
                                        ),
                                      },
                                    }}
                                  >
                                    {loadingArticleId === article.id ? (
                                      <CircularProgress
                                        size={20}
                                        color="primary"
                                      />
                                    ) : (
                                      <VisibilityIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>

                              <Tooltip title="Edit Article">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() =>
                                    handleOpenEditArticleModal(article)
                                  }
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: alpha(
                                        theme.palette.warning.main,
                                        0.1
                                      ),
                                    },
                                  }}
                                >

                                   {loadingArticleId1 === article.id ? (
                                      <CircularProgress
                                        size={20}
                                        color="primary"
                                      />
                                    ) : (
                                      <EditIcon fontSize="small" />
                                    )}
                                     
                                  
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Article">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteClick(article)}
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: (theme) =>
                                        alpha(theme.palette.error.main, 0.1),
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </ModernTableCell>
                        </StyledTableRow>
                      ))
                  ) : (
                    <TableRow>
                      <ModernTableCell
                        colSpan={5}
                        align="center"
                        sx={{ py: 4 }}
                      >
                        <Typography color="textSecondary">
                          {searchTerm
                            ? "No matching articles found"
                            : "No articles available"}
                        </Typography>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={handleOpenCreateModal}
                          size="small"
                          sx={{ mt: 1 }}
                        >
                          Create your first article
                        </Button>
                      </ModernTableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ResponsiveTableContainer>

            {ReduxArticles.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={Math.ceil(ReduxArticles.length / rowsPerPage)}
                  page={page}
                  onChange={handleChangePage}
                  color="primary"
                  shape="rounded"
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    "& .MuiPaginationItem-root": { borderRadius: "999px" },
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </MainContent>

       <Dialog
  open={createModalOpen}
  onClose={handleCloseCreateModal}
  maxWidth="md"
  fullWidth
  fullScreen={isMobile}
  slotProps={{
    backdrop: {
      sx: {
        backgroundColor: "rgba(0, 0, 0, 0.6) !important", // Add !important to ensure black overlay applies
      },
    },
    modal: {
      keepMounted: true, // ensures backdrop mounts correctly
    },
  }}
  PaperProps={{
    sx: {
      ...(isMobile
        ? {
            borderRadius: 0,
            background: "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)",
            boxShadow: "none",
          }
        : {
            borderRadius: "16px",
            background: "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
          }),
      maxHeight: "90vh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    },
  }}
>
  <DialogTitle
    sx={{
      p: 0,
      position: "relative",
      "&:before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "4px",
        background: "linear-gradient(90deg, #3f51b5 0%, #2196f3 100%)",
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 3,
        pb: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            background: "linear-gradient(90deg, #3f51b5 0%, #2196f3 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            display: "inline-block",
          }}
        >
          Create New Article
        </Typography>
      </Box>
      <IconButton
        onClick={handleCloseCreateModal}
        sx={{
          color: "text.secondary",
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  </DialogTitle>
  <DialogContent
    dividers
    sx={{
      flexGrow: 1,
      overflowY: "auto",
      p: isMobile ? 2 : 3,
    }}
  >
    <TextField
      label="Title"
      name="title"
      fullWidth
      size="small"
      value={formData.title}
      onChange={handleInputChange}
      sx={{ mb: 2 }}
      error={formErrors.title}
      helperText={formErrors.title ? "Title is required" : ""}
      InputProps={{
        endAdornment: formData.title && (
          <InputAdornment position="end">
            <CheckCircleIcon color="success" fontSize="small" />
          </InputAdornment>
        ),
      }}
    />
    <Select
      fullWidth
      size="small"
      name="moduleId"
      value={formData.moduleId}
      onChange={handleInputChange}
      displayEmpty
      sx={{ mb: 2 }}
      error={formErrors.moduleId}
      renderValue={(selected) => {
        if (!selected) {
          return (
            <em
              style={{
                color: formErrors.moduleId
                  ? "#f44336"
                  : "rgba(0, 0, 0, 0.6)",
              }}
            >
              Select Module
            </em>
          );
        }
        return (
          ReduxCategories.find((c) => c.id === selected)?.categoryName ||
          selected
        );
      }}
    >
      <MenuItem value="" disabled>
        Select Module
      </MenuItem>
      {isLoading ? (
        <MenuItem disabled>Loading...</MenuItem>
      ) : (
        ReduxCategories.map((category) => (
          <MenuItem key={category.id} value={category.id}>
            {category.categoryName}
          </MenuItem>
        ))
      )}
    </Select>
    <Box sx={{ mb: 2 }}>
      <TextEditorComponent
        ref={createEditorRef}
        onChange={handleEditorChange}
        initialContent={null}
        maxCharCount={10000}
      />
      {formErrors.content && (
        <Typography color="error" variant="caption">
          Content is required
        </Typography>
      )}
    </Box>
  </DialogContent>
  <DialogActions sx={{ p: 2, justifyContent: "flex-end", gap: 1 }}>
    <Button
      variant="outlined"
      color="secondary"
      onClick={handleCloseCreateModal}
      disabled={loading}
    >
      Cancel
    </Button>
    <Button
      variant="contained"
      color="primary"
      onClick={handleCreateArticle}
      disabled={loading}
      startIcon={loading ? <CircularProgress size={20} /> : null}
      sx={{ minWidth: 120 }}
    >
      {loading ? "Creating..." : "Create Article"}
    </Button>
  </DialogActions>
</Dialog>

     {/* Edit Article Dialog */}
<Dialog
  open={editModalOpen}
  onClose={() => setEditModalOpen(false)}
  maxWidth="md"
  fullWidth
  fullScreen={isMobile}
  slotProps={{
    backdrop: {
      sx: {
        backgroundColor: "rgba(0, 0, 0, 0.6) !important", // Add black overlay with high specificity
      },
    },
  }}
  PaperProps={{
    sx: {
      ...(isMobile
        ? {
            borderRadius: 0,
            background: "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)",
            boxShadow: "none",
          }
        : {
            position: "absolute",
            top: "45%",
            left: "58%",
            transform: "translate(-50%, -50%)",
            borderRadius: "16px",
            background: "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
          }),
      maxHeight: "90vh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    },
  }}
>
  <DialogTitle
    sx={{
      p: 0,
      position: "relative",
      "&:before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "4px",
        background: "linear-gradient(90deg, #3f51b5 0%, #2196f3 100%)",
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 3,
        pb: 2,
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          background: "linear-gradient(90deg, #3f51b5 0%, #2196f3 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          display: "inline-block",
        }}
      >
        Edit Article
      </Typography>
      <IconButton
        onClick={() => setEditModalOpen(false)}
        sx={{
          color: "text.secondary",
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  </DialogTitle>
  <DialogContent
    dividers
    sx={{
      flexGrow: 1,
      overflowY: "auto",
      p: isMobile ? 2 : 3,
    }}
  >
    <TextField
      label="Title"
      name="title"
      fullWidth
      size="small"
      value={formData.title}
      onChange={handleInputChange}
      sx={{ mb: 2 }}
      error={formErrors.title}
      helperText={formErrors.title ? "Title is required" : ""}
      InputProps={{
        endAdornment: formData.title && (
          <InputAdornment position="end">
            <CheckCircleIcon color="success" fontSize="small" />
          </InputAdornment>
        ),
      }}
    />
    <Select
      fullWidth
      size="small"
      name="moduleId"
      value={formData.moduleId}
      onChange={handleInputChange}
      displayEmpty
      sx={{ mb: 2 }}
      error={formErrors.moduleId}
      renderValue={(selected) => {
        if (!selected) {
          return (
            <em
              style={{
                color: formErrors.moduleId ? "#f44336" : "rgba(0, 0, 0, 0.6)",
              }}
            >
              Edit a module
            </em>
          );
        }
        return (
          ReduxCategories.find((c) => c.id === selected)?.categoryName || selected
        );
      }}
    >
      <MenuItem value="" disabled>
        Select a module to Edit
      </MenuItem>
      {isLoading ? (
        <MenuItem disabled>Loading...</MenuItem>
      ) : (
        ReduxCategories.map((category) => (
          <MenuItem key={category.id} value={category.id}>
            {category.categoryName}
          </MenuItem>
        ))
      )}
    </Select>
    <Box sx={{ mb: 2 }}>
      <TextEditorComponent
        ref={editEditorRef}
        onChange={handleEditorChange}
        initialContent={formData.htmlContent}
        maxCharCount={10000}
      />
      {formErrors.content && (
        <Typography color="error" variant="caption">
          Content is required
        </Typography>
      )}
    </Box>
  </DialogContent>
  <DialogActions sx={{ p: 2, justifyContent: "flex-end", gap: 1 }}>
    <Button
      variant="outlined"
      color="secondary"
      onClick={() => setEditModalOpen(false)}
      disabled={loading}
    >
      Cancel
    </Button>
    <Button
      variant="contained"
      color="primary"
      onClick={handleUpdateArticle}
      disabled={loading}
      startIcon={loading ? <CircularProgress size={20} /> : null}
      sx={{ minWidth: 120 }}
    >
      {loading ? "Updating..." : "Update Article"}
    </Button>
  </DialogActions>
</Dialog>

       {/* Preview Dialog */}
<Dialog
  open={previewOpen}
  onClose={handleClosePreview}
  maxWidth="md"
  fullWidth
  fullScreen={isMobile}
  slotProps={{
    backdrop: {
      sx: {
        backgroundColor: "rgba(0, 0, 0, 0.6) !important", // Add black overlay with high specificity
      },
    },
  }}
>
  <Box sx={{ position: 'relative' }}>
     
<IconButton
  aria-label="close"
  onClick={handleClosePreview}
  sx={{
    position: 'absolute',
    right: 24,
    top: 24,
    zIndex: 1,
    color: 'white',
    backgroundColor: 'rgba(255,255,255,0.1)',
    backdropFilter: 'blur(4px)',
    borderRadius: '50%',
    border: '1px solid rgba(255,255,255,0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.2)',
      transform: 'scale(1.1)',
      boxShadow: '0 0 15px rgba(255,255,255,0.4)'
    },
    '& svg': {
      fontSize: '1.5rem'
    }
  }}
>
  <CloseIcon />
</IconButton>

{/* Futuristic Dialog Title */}
 <DialogTitle sx={{ 
  pt: 7,
  pb: 3,
  backgroundColor: 'primary.main',
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 1, // Adds space between title and chip
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '2px',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)'
  }
}}>
  <Box sx={{ width: '100%' }}>
    <Typography 
      variant="h5" 
      sx={{
        fontWeight: 600,
        letterSpacing: '0.5px',
        position: 'relative',
        display: 'inline-block',
        '&:after': {
          content: '""',
          position: 'absolute',
          bottom: -8,
          left: 0,
          width: '40px',
          height: '3px',
          background: 'white',
          borderRadius: '3px'
        }
      }}
    >
      {formData.title}
    </Typography>
  </Box>
  
  <Chip 
    label={formData.module || "Hello test"} 
    sx={{ 
      color: '#1976D2',
      backgroundColor: 'white',
      fontWeight: 600,
      height: '28px',
      borderRadius: '14px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      '& .MuiChip-label': {
        px: 1.5,
        py: 0.5
      }
    }} 
  />
</DialogTitle>
    
    <DialogContent dividers>
      <Box
        sx={{
          p: 2,
          "& img": { maxWidth: "100%" },
          "& pre": {
            backgroundColor: "#f5f5f5",
            padding: "16px",
            borderRadius: "4px",
            overflowX: "auto",
          },
        }}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(formData.htmlContent),
        }}
      />
    </DialogContent>
    
    {/* Remove DialogActions if you don't want the bottom Close button */}
    {/* <DialogActions>
      <Button onClick={handleClosePreview}>Close</Button>
    </DialogActions> */}
  </Box>
</Dialog>

      {/* Confirmation Dialog for Deletion */}
      <ConfirmationDialog
        open={openConfirm}
        handleClose={() => setOpenConfirm(false)}
        handleConfirm={handleDeleteConfirm}
        title="Confirm Deletion"
        content={`Are you sure you want to delete the article titled "${articleToDelete?.title}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        loading={loading}
        severity="error"
      />

      {/* Snackbar for notifications */}
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </MainContainer>
  );
}

export default Articles;
